// src/features/projects/weekly/myUpdateModel.js
//
// PMO Slice C — pure helpers behind the "My weekly update" contributor card.
// Membership is exact: a track is editable only when the caller holds that
// project+track assignment AND the server capability allows it (the server
// stays the gate; this only decides which controls render).

import { TRACKS, resolveLine } from "./weeklyModel.js";
import { OWNERSHIP_SLOTS } from "../../../lib/projectTracks.js";

export const WRITE_KIND = Object.freeze({ AUTH: "auth", AMBIGUOUS: "ambiguous", REJECTED: "rejected" });

export function priorMonday(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 7);
  return d.toISOString().slice(0, 10);
}

/** Group raw /my/assignments rows into one entry per project (stable name order). */
export function groupAssignments(assignments) {
  const map = new Map();
  for (const a of Array.isArray(assignments) ? assignments : []) {
    if (!a?.projectId) continue;
    const slot = a.ownershipSlot || "delivery_owner";
    if (a.trackKey == null && !OWNERSHIP_SLOTS.some(s => s.key === slot)) continue;
    if (a.trackKey != null && !TRACKS.some(t => t.key === a.trackKey)) continue;
    if (!map.has(a.projectId)) map.set(a.projectId, { projectId: a.projectId, projectName: a.projectName || "Untitled project", personId: a.personId, isOwner: false, ownershipSlots: [], trackKeys: [] });
    const e = map.get(a.projectId);
    if (a.trackKey == null) {
      if (!e.ownershipSlots.includes(slot)) e.ownershipSlots.push(slot);
      if (slot === "delivery_owner") e.isOwner = true;
    }
    else if (!e.trackKeys.includes(a.trackKey)) e.trackKeys.push(a.trackKey);
  }
  const order = TRACKS.map((t) => t.key);
  return [...map.values()]
    .map((e) => ({ ...e, trackKeys: e.trackKeys.sort((x, y) => order.indexOf(x) - order.indexOf(y)) }))
    .sort((a, b) => a.projectName.localeCompare(b.projectName, "en", { sensitivity: "base" }) || (a.projectId < b.projectId ? -1 : 1));
}

export function myPersonId(assignments) {
  return (Array.isArray(assignments) ? assignments : []).find((a) => a?.personId)?.personId || null;
}

/** Tracks the caller may edit: exact assignment AND server capability, never ownership alone. */
export function editableTrackKeys(entry, capabilities, writable) {
  if (!writable || !entry) return [];
  const allowed = new Set(Array.isArray(capabilities?.canSetTrackStatus) ? capabilities.canSetTrackStatus : []);
  return entry.trackKeys.filter((k) => allowed.has(k));
}

export function canEditSummary(entry, capabilities, writable) {
  return Boolean(writable && entry?.isOwner && capabilities?.canSummary);
}

/** Owner sees all canonical tracks; contributors see their tracks + read-only others. */
export function visibleTracks(entry) {
  if (!entry) return { mine: [], others: [] };
  const mine = TRACKS.filter((t) => entry.trackKeys.includes(t.key));
  const others = TRACKS.filter((t) => !entry.trackKeys.includes(t.key));
  return entry.isOwner ? { mine, others, ownerSeesAll: true } : { mine, others, ownerSeesAll: false };
}

/** Actual prior-week content authored by `personId`, after corrections; struck lines excluded. */
export function priorAuthoredTexts(priorProject, personId, key) {
  if (!priorProject || !personId) return [];
  return (priorProject.lines || [])
    .filter((l) => l.trackKey === key && l.authorPersonId === personId)
    .map((l) => resolveLine(l))
    .filter((r) => !r.struck && r.current)
    .map((r) => r.current);
}

export function currentTexts(project, key) {
  return (project?.lines || [])
    .filter((l) => l.trackKey === key)
    .map((l) => resolveLine(l))
    .filter((r) => !r.struck && r.current)
    .map((r) => r.current);
}

/** Explainable overall suggestion from visible current-week track statuses. Never auto-applied. */
export function suggestOverall(statuses) {
  const list = (Array.isArray(statuses) ? statuses : []).filter((s) => s?.status);
  if (list.length === 0) return null;
  const label = (s) => TRACKS.find((t) => t.key === s.trackKey)?.label || s.trackKey;
  const blocked = list.filter((s) => s.status === "blocked");
  if (blocked.length) return { value: "critical", reason: `${blocked.map(label).join(", ")} ${blocked.length === 1 ? "is" : "are"} blocked` };
  const watch = list.filter((s) => s.status === "watch");
  if (watch.length) return { value: "at_risk", reason: `${watch.map(label).join(", ")} on watch` };
  return { value: "on_track", reason: `all ${list.length} reported track${list.length === 1 ? "" : "s"} on track` };
}

/** Deterministic summary prefill from actual current-week lines (canonical track order). */
export function draftFromTracks(project) {
  return TRACKS.map((t) => {
    const texts = currentTexts(project, t.key);
    return texts.length ? `${t.label}: ${texts.join(" ")}` : null;
  }).filter(Boolean).join("\n");
}

export function classifyWriteError(err) {
  if (err?.name === "AuthError") return WRITE_KIND.AUTH;
  if (err?.status >= 400 && err.status < 500) return WRITE_KIND.REJECTED;
  // No HTTP response (or a 5xx) means we cannot know whether an append landed.
  if (err?.name === "NetworkError" || !err?.name || err.name === "Error") return WRITE_KIND.AMBIGUOUS;
  return WRITE_KIND.REJECTED;
}

/** Serialises async work per key so saves to the same project land in order. */
export function createSerialQueue() {
  const tails = new Map();
  const run = (key, task) => {
    const prev = tails.get(key) || Promise.resolve();
    const next = prev.catch(() => {}).then(task);
    tails.set(key, next);
    next.finally(() => { if (tails.get(key) === next) tails.delete(key); }).catch(() => {});
    return next;
  };
  const drain = async () => {
    while (tails.size) await Promise.allSettled([...tails.values()]);
  };
  return { run, drain, pending: () => tails.size };
}

/**
 * Single-flight guard for appends: blur followed by Enter (or a double Enter)
 * for the same draft must post exactly once. Ambiguous outcomes lock the key
 * until a refetch resolves them, so a draft is never blindly re-appended.
 */
export function createAppendGuard() {
  const inflight = new Set();
  const locked = new Set();
  return {
    tryBegin(key) {
      if (inflight.has(key) || locked.has(key)) return false;
      inflight.add(key);
      return true;
    },
    end(key) { inflight.delete(key); },
    lock(key) { locked.add(key); },
    unlock(key) { locked.delete(key); },
    isBusy(key) { return inflight.has(key); },
    isLocked(key) { return locked.has(key); },
  };
}

/** After an ambiguous append, did a line from me with this exact text land since `knownIds`? */
export function findLandedLine(project, personId, key, note, knownIds) {
  const text = (note || "").trim();
  const known = new Set(knownIds || []);
  return (project?.lines || []).find((l) => l.trackKey === key && l.authorPersonId === personId && (l.note || "").trim() === text && !known.has(l.id)) || null;
}

export function isCompletedBy(project, personId) {
  return Boolean(personId && (project?.completions || []).some((c) => c.personId === personId));
}
